import {
  BarChart3,
  Hash,
  Layers,
  Radio,
  ShieldCheck,
  Smartphone,
  Trophy,
  type LucideIcon,
} from 'lucide-react'

import { SectionLabel } from '@/features/landing/components/SectionLabel'
import { cn } from '@/lib/utils'

interface USP {
  index: string
  title: string
  body: string
  icon: LucideIcon
  /** Tailwind class controlling card span in the 6-col mosaic. */
  span: string
  /** Pull-quote treatment with larger type. */
  feature?: boolean
  /** Accent colour family — sparingly used on icons. */
  accent: 'primary' | 'secondary' | 'tertiary'
}

const USPS: USP[] = [
  // Tournaments lead the grid — the outbound clan-leader push majors on them,
  // and its email lands here. Order mirrors LEADERS_PACK (e404_infra/marketing).
  {
    index: '01',
    title: 'Tournaments that referee themselves',
    body:
      'Single-elim, double-elim, Swiss, round-robin. Both captains confirm the score, or the match flags DISPUTED and routes to the host — the event thread keeps the argument on the record, and every override is audit-logged. No Challonge link taped on the side, no refereeing DMs at midnight.',
    icon: Trophy,
    span: 'md:col-span-3 lg:col-span-4 lg:row-span-2',
    feature: true,
    accent: 'primary',
  },
  {
    index: '02',
    title: 'Everything on one screen',
    body:
      'Roster, bracket, Discord, Twitch, dues — same app, from sign-up to the trophy lift. Nobody has to alt-tab to find what’s happening tonight.',
    icon: Layers,
    span: 'md:col-span-3 lg:col-span-2',
    accent: 'secondary',
  },
  {
    index: '03',
    title: 'Live stream discovery',
    body:
      'The moment a squadmate goes live on Twitch or YouTube, you’ll see it here. Watch the bracket and the stream side by side, no extra tab.',
    icon: Radio,
    span: 'md:col-span-3 lg:col-span-2',
    accent: 'primary',
  },
  {
    index: '04',
    title: 'Discord that just works',
    body:
      'Link your Discord server once. Events bind to channels automatically, so notifications land where your squad already talks — no extra setup for you.',
    icon: Hash,
    span: 'md:col-span-3 lg:col-span-2',
    accent: 'tertiary',
  },
  {
    index: '05',
    title: 'Stats that follow you',
    body:
      'Every match adds to your record — wins, losses, head-to-head against every rival you’ve faced. Per-game stats, match log, opponent history, all saved under your name.',
    icon: BarChart3,
    span: 'md:col-span-3 lg:col-span-2',
    accent: 'secondary',
  },
  {
    index: '06',
    title: 'Verified squads',
    body:
      'A squad badge nobody can fake, because getting one needs a real institutional inbox. Prospects know exactly who they’re joining.',
    icon: ShieldCheck,
    span: 'md:col-span-3 lg:col-span-2',
    accent: 'primary',
  },
  {
    index: '07',
    title: 'Mobile-first. Web-capable.',
    body:
      'One account works everywhere e404 does, mobile and web. Approve a join request from the bus, lock in a bracket from the gym, or override a result on the train — wherever you are.',
    icon: Smartphone,
    span: 'md:col-span-6 lg:col-span-6',
    accent: 'secondary',
  },
]

const ACCENT_STYLES = {
  primary: 'border-primary/30 bg-primary/10 text-primary',
  secondary: 'border-secondary/30 bg-secondary/10 text-secondary',
  tertiary: 'border-tertiary/30 bg-tertiary/10 text-tertiary',
} as const

export function USPGrid() {
  return (
    <section
      id="why"
      className="relative border-b border-border/60 px-6 py-24 md:py-32"
    >
      <div className="mx-auto max-w-6xl">
        <div className="mb-12 flex flex-col items-start gap-6 md:mb-16">
          <SectionLabel index="03" label="Why e404" />
          <h2 className="font-display max-w-3xl text-4xl leading-[1.05] font-semibold text-balance md:text-6xl">
            Everything your squad does, on one screen.
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
          {USPS.map((usp) => (
            <USPCard key={usp.index} usp={usp} />
          ))}
        </div>
      </div>
    </section>
  )
}

function USPCard({ usp }: { usp: USP }) {
  const Icon = usp.icon
  const isFeature = usp.feature

  return (
    <article
      className={cn(
        'group relative flex flex-col gap-4 rounded-xl border border-border bg-surface p-6 transition-all duration-300',
        'hover:border-fg-subtle/50 hover:bg-surface-hover hover:-translate-y-0.5',
        isFeature && 'md:p-8',
        usp.span,
      )}
    >
      {/* Top row: index + accent-tinted icon chip */}
      <div className="flex items-center justify-between">
        <span className="font-display text-xs tracking-[0.25em] text-fg-subtle uppercase tabular-nums">
          // {usp.index}
        </span>
        <span
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-lg border',
            ACCENT_STYLES[usp.accent],
          )}
        >
          <Icon className="size-4" strokeWidth={1.75} aria-hidden />
        </span>
      </div>

      <div className="flex flex-col gap-3">
        <h3
          className={cn(
            'font-display font-semibold tracking-tight text-fg text-balance',
            isFeature ? 'text-3xl md:text-4xl' : 'text-xl',
          )}
        >
          {usp.title}
        </h3>
        <p
          className={cn(
            'text-fg-muted text-pretty',
            isFeature ? 'text-base md:text-lg' : 'text-sm',
          )}
        >
          {usp.body}
        </p>
      </div>

      {/* Decorative corner mark — small tactical detail repeated per card */}
      <span
        aria-hidden
        className="pointer-events-none absolute right-3 bottom-3 h-2 w-2 border-r border-b border-fg-subtle/30"
      />
    </article>
  )
}
